import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Package, Plus, RefreshCw, Search, Trash2, Edit, X,
  TrendingUp, TrendingDown, History
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '', brand: 'Belzona', category: '', sku: '',
  description: '', quantity: 0, unit: 'ea', location: '', notes: ''
};

const CRMInventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('quantity_desc'); // quantity_desc | quantity_asc | name_asc | name_desc | recent
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustVal, setAdjustVal] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [historyItem, setHistoryItem] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/crm/inventory${search ? `?search=${encodeURIComponent(search)}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setItems(data.items || []);
    } catch {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setForm({
      name: item.name || '', brand: item.brand || 'Belzona',
      category: item.category || '', sku: item.sku || '',
      description: item.description || '', quantity: item.quantity || 0,
      unit: item.unit || 'ea', location: item.location || '', notes: item.notes || ''
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...form, quantity: parseFloat(form.quantity) || 0 };
    try {
      const url = editId ? `${API}/api/crm/inventory/${editId}` : `${API}/api/crm/inventory`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success(editId ? 'Updated' : 'Added');
      setShowForm(false); setEditId(null); setForm({ ...emptyForm });
      load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await fetch(`${API}/api/crm/inventory/${id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    const delta = parseFloat(adjustVal);
    if (!delta || Number.isNaN(delta)) return toast.error('Enter quantity (use - for stock out)');
    try {
      await fetch(`${API}/api/crm/inventory/${adjustItem.id}/adjust`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delta, note: adjustNote }),
      });
      toast.success(`Stock ${delta > 0 ? 'added' : 'removed'}: ${Math.abs(delta)}`);
      setAdjustItem(null); setAdjustVal(''); setAdjustNote('');
      load();
    } catch { toast.error('Failed'); }
  };

  const openHistory = async (item) => {
    setHistoryItem(item);
    try {
      const res = await fetch(`${API}/api/crm/inventory/${item.id}/history`, { credentials: 'include' });
      const data = await res.json();
      setHistoryLogs(data.history || []);
    } catch { setHistoryLogs([]); }
  };

  const filtered = (() => {
    const arr = [...items];
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'quantity_desc': return (b.quantity || 0) - (a.quantity || 0);
        case 'quantity_asc':  return (a.quantity || 0) - (b.quantity || 0);
        case 'name_asc':      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        case 'name_desc':     return (b.name || '').localeCompare(a.name || '', undefined, { sensitivity: 'base' });
        case 'recent':        return (b.updated_at || '').localeCompare(a.updated_at || '');
        default:              return 0;
      }
    });
    return arr;
  })();
  const totalItems = filtered.length;
  const totalQty = filtered.reduce((s, i) => s + (i.quantity || 0), 0);

  return (
    <div className="space-y-4" data-testid="crm-inventory">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Package size={20} className="text-blue-900" /> Inventory
          </h2>
          <p className="text-sm text-gray-500">{totalItems} products · {totalQty.toLocaleString()} total units in stock</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="inventory-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" className="bg-blue-900 hover:bg-blue-800"
            onClick={() => { setForm({ ...emptyForm }); setEditId(null); setShowForm(true); }}
            data-testid="add-inventory-btn">
            <Plus size={14} className="mr-1" /> Add Product
          </Button>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, SKU, or description…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            data-testid="inventory-search" />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          data-testid="inventory-sort"
        >
          <option value="quantity_desc">Available: High → Low</option>
          <option value="quantity_asc">Available: Low → High</option>
          <option value="name_asc">Name: A → Z</option>
          <option value="name_desc">Name: Z → A</option>
          <option value="recent">Recently Updated</option>
        </select>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="border-blue-200">
          <CardContent className="pt-5">
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Product Name *</Label>
                  <input required value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g., Belzona 1111 Super Metal"
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="inv-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Brand</Label>
                  <input value={form.brand}
                    onChange={(e) => setForm(f => ({ ...f, brand: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Category</Label>
                  <input value={form.category}
                    onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                    placeholder="Coatings, Repair Composites, etc."
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">SKU / Part #</Label>
                  <input value={form.sku}
                    onChange={(e) => setForm(f => ({ ...f, sku: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Starting Quantity</Label>
                  <input type="number" step="any" value={form.quantity}
                    onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="inv-qty" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Unit</Label>
                  <input value={form.unit}
                    onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))}
                    placeholder="ea, kg, gal, kit"
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Location</Label>
                  <input value={form.location}
                    onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                    placeholder="Shelf A1, Warehouse, etc."
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">Description</Label>
                  <textarea rows={2} value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="bg-blue-900 hover:bg-blue-800" data-testid="save-inventory-btn">
                  {editId ? 'Update' : 'Add Product'}
                </Button>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Adjust Stock Modal */}
      {adjustItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAdjustItem(null)}>
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardContent className="pt-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">Adjust Stock</h3>
                  <p className="text-xs text-gray-500">{adjustItem.name} · Current: {adjustItem.quantity || 0} {adjustItem.unit}</p>
                </div>
                <button onClick={() => setAdjustItem(null)}><X size={18} className="text-gray-400" /></button>
              </div>
              <form onSubmit={submitAdjust} className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Quantity Change (negative for stock out)</Label>
                  <input type="number" step="any" autoFocus value={adjustVal}
                    onChange={(e) => setAdjustVal(e.target.value)}
                    placeholder="e.g., 10 or -5"
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="adjust-delta" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Note (optional)</Label>
                  <input value={adjustNote}
                    onChange={(e) => setAdjustNote(e.target.value)}
                    placeholder="Used on project XYZ, restock from vendor, etc."
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm" className="bg-blue-900 hover:bg-blue-800" data-testid="submit-adjust-btn">Apply</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setAdjustItem(null)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Modal */}
      {historyItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setHistoryItem(null)}>
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardContent className="pt-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">Stock History</h3>
                  <p className="text-xs text-gray-500">{historyItem.name}</p>
                </div>
                <button onClick={() => setHistoryItem(null)}><X size={18} className="text-gray-400" /></button>
              </div>
              {historyLogs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No adjustments recorded yet</p>
              ) : (
                <div className="space-y-2">
                  {historyLogs.map(log => (
                    <div key={log.id} className="flex items-center justify-between py-2 px-3 border rounded-lg text-sm">
                      <div>
                        <div className="flex items-center gap-2">
                          {log.delta > 0
                            ? <Badge className="bg-emerald-100 text-emerald-800 text-xs"><TrendingUp size={11} className="mr-1" /> +{log.delta}</Badge>
                            : <Badge className="bg-red-100 text-red-800 text-xs"><TrendingDown size={11} className="mr-1" /> {log.delta}</Badge>}
                          <span className="text-xs text-gray-500">{log.previous_qty} → {log.new_qty}</span>
                        </div>
                        {log.note && <p className="text-xs text-gray-600 mt-1">{log.note}</p>}
                      </div>
                      <span className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Items list */}
      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-400">
            <Package className="mx-auto mb-2" size={32} />
            <p>No inventory items {search ? 'match your search' : 'yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const qty = item.quantity || 0;
            const qtyColor = qty <= 0 ? 'text-red-600' : qty < 5 ? 'text-amber-600' : 'text-emerald-700';
            return (
              <Card key={item.id} className="hover:shadow-sm" data-testid={`inventory-item-${item.id}`}>
                <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-slate-900">{item.name}</h4>
                      {item.brand && <Badge className="bg-slate-100 text-slate-700 text-[10px]">{item.brand}</Badge>}
                      {item.category && <Badge className="bg-blue-50 text-blue-700 text-[10px]">{item.category}</Badge>}
                      {item.sku && <span className="text-xs font-mono text-gray-400">{item.sku}</span>}
                    </div>
                    {item.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.description}</p>}
                    {item.location && <p className="text-xs text-gray-400 mt-0.5">📍 {item.location}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-xl font-bold ${qtyColor}`}>{qty}</p>
                    <p className="text-xs text-gray-400">{item.unit || 'ea'}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setAdjustItem(item)} data-testid={`adjust-${item.id}`}>
                      <TrendingUp size={13} className="mr-1" /> Adjust
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openHistory(item)} title="History">
                      <History size={13} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => startEdit(item)} title="Edit">
                      <Edit size={13} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400" onClick={() => handleDelete(item.id)} title="Delete">
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CRMInventory;
